/**
 * A `[startMs, endMs]` span (epoch ms) we assume covered for a given stream+resolution.
 *
 * On a cache entry these are kept sorted by `startMs` and fully coalesced (no overlapping or
 * touching pairs) by `mergeCoveredRanges`, so gap detection can assume a clean, ordered list.
 */
export type CoveredRange = { startMs: number; endMs: number };

/**
 * Decides how much of a just-fetched window `[from, to]` is safe to assume covered, so we
 * stop endlessly re-fetching it. A window is *sealed* once it is old enough that late data is
 * no longer expected (see `sealDelayMs`); until then it is *unsealed*, where an empty result
 * near `now` may just mean "not yet" rather than "nothing ever".
 *
 * @param cursor  newest datum's `occurredAt` (epoch ms), or `null` if the fetch was empty.
 * @param sealDelayMs how long after a window's `to` we keep re-fetching to catch late-arriving
 *                data; past that the window is *sealed* and we stop.
 * @returns the `CoveredRange` to record, or `null` to record nothing (re-query later).
 */
export function coveredRange(
    from: number,
    to: number,
    cursor: number | null,
    now: number,
    sealDelayMs: number,
): CoveredRange | null {
    const isSealed = to < now - sealDelayMs;

    // Sealed: late data is no longer expected, so cover the whole window — an empty fetch too,
    // since that absence is now permanent.
    if (isSealed) {
        return { startMs: from, endMs: to };
    }

    // Unsealed + empty: can't tell "nothing yet" from "nothing ever", so cover nothing.
    if (cursor === null) {
        return null;
    }

    // Unsealed + data: everything through `cursor` is real; leave (cursor, to] open for late readings.
    return { startMs: from, endMs: cursor };
}
